import { useEffect, useState } from "react";
import { api, type JobseekerDiscovery, type JobseekerMissingRequirement } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Link } from "wouter";
import { Check, ChevronRight } from "lucide-react";
import { Switch } from "./beui/Switch";
import { BouncyAccordion } from "./beui/BouncyAccordion";
import { MobileSettingsSwitch } from "./MobileSettingsControls";

const requirementLabels: Record<JobseekerMissingRequirement, string> = {
  find_work: "Find Work activity enabled",
  headlineOrPrimaryRole: "Headline or primary profession",
  bio: "Biography",
  city: "City",
  skills: "At least one skill",
  yearsExperience: "Years of experience (zero is accepted)",
  activeResume: "Active résumé available for download",
};

export function JobseekerDiscoveryControl({ userId, mobileSettings = false, requirementsOnly = false, beui = false }: { userId: number; mobileSettings?: boolean; requirementsOnly?: boolean; beui?: boolean }) {
  const { t } = useI18n();
  const [discovery, setDiscovery] = useState<JobseekerDiscovery | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setDiscovery(null);
    setError("");
    api.getJobseekerDiscovery()
      .then((value) => { if (active) setDiscovery(value); })
      .catch(() => { if (active) setError(requirementsOnly ? "Could not load Jobseeker requirements. Reload to try again." : "Could not load Jobseeker discovery. Reload to try again."); });
    return () => { active = false; };
  }, [userId, requirementsOnly]);

  async function changeDiscovery(enabled: boolean) {
    setSaving(true);
    setError("");
    try {
      setDiscovery(await api.setJobseekerDiscovery(enabled));
    } catch (failure: unknown) {
      setError(failure instanceof Error ? failure.message : "Could not save Jobseeker discovery.");
      try { setDiscovery(await api.getJobseekerDiscovery()); } catch { /* Keep the last known state. */ }
    } finally {
      setSaving(false);
    }
  }

  const selected = !!discovery && (discovery.enabled || discovery.legacyVisible);
  const missingFindWork = discovery?.missing.includes("find_work") ?? false;

  const hrefFor=(key:JobseekerMissingRequirement)=>key==='find_work'?'/choose-activities?focus=find_work':key==='activeResume'?'/settings/profile?field=resume':'/worker-profile?field='+encodeURIComponent(key);
  const requirementList=discovery&&<ul className={beui?'settings-a-requirements':'mt-2 space-y-1 text-mut'}>{(Object.keys(requirementLabels) as JobseekerMissingRequirement[]).map(key=><li key={key} className="flex gap-2">{discovery.missing.includes(key)?<Link href={hrefFor(key)} className="inline-flex min-h-11 items-center justify-between gap-2 text-blue-700"><span>{requirementLabels[key]}</span><span>Needed<ChevronRight className="h-4 w-4" aria-hidden="true"/></span></Link>:<><Check className="h-4 w-4 shrink-0 text-brand-green" aria-hidden="true"/><span>{requirementLabels[key]}</span></>}</li>)}</ul>;


  return <>
    {error && <p role="alert" className="text-sm text-brand-red mt-3">{error}</p>}
    {!discovery && !error && <p role="status" className="text-sm text-mut mt-3">{requirementsOnly ? "Loading Jobseeker requirements…" : "Loading discovery preference…"}</p>}
    {discovery && <>
      {!requirementsOnly && (beui ? <div className="settings-a-row"><div><h3>{t('settings.showInJobseekers')}</h3></div><Switch ariaLabel={t('settings.showInJobseekers')} checked={selected} disabled={saving||(missingFindWork&&!selected)} onCheckedChange={value=>void changeDiscovery(value)}/></div> : mobileSettings ? <MobileSettingsSwitch title={t("settings.discoveryTitle")} description={t("settings.discoveryDescription")} checked={selected} disabled={saving || (missingFindWork && !selected)} onChange={(enabled) => { void changeDiscovery(enabled); }} /> : <label className="flex items-center gap-3 mt-3 font-semibold text-sm">
        <input type="checkbox" checked={selected} disabled={saving || (missingFindWork && !selected)}
          onChange={(event) => { void changeDiscovery(event.target.checked); }} />
        {t("settings.showInJobseekers")}
      </label>)}
      {!requirementsOnly && <p className="text-sm text-mut mt-3" role="status">
        {discovery.effective === "visible" ? t("settings.discoveryVisible", "You are visible in Jobseekers.") : discovery.effective === "off"
          ? t("settings.discoveryOff")
          : "Your on choice is saved, but discovery is suppressed. Set availability to Available and complete the requirements to appear."}
      </p>}
      {!requirementsOnly && discovery.legacyVisible && <p className="text-sm text-mut mt-2">Your existing résumé listing keeps you eligible for discovery. Turning this off ends that legacy visibility; turning it on again requires all fields below.</p>}
      {beui?<BouncyAccordion classNames={{title:"settings-requirement-heading"}} key={discovery.missing.join(',')} defaultValue={discovery.missing.length?'requirements':null} items={[{id:'requirements',title:'Jobseekers requirements · '+(Object.keys(requirementLabels).length-discovery.missing.length)+'/'+Object.keys(requirementLabels).length+' complete',description:requirementList}]}/>:<details className="mt-4 text-sm" key={discovery.missing.join(',')} open={discovery.missing.length>0}><summary className="cursor-pointer font-semibold text-navy">Jobseekers requirements ({Object.keys(requirementLabels).length-discovery.missing.length}/{Object.keys(requirementLabels).length} complete)</summary>{requirementList}</details>}
    </>}
  </>;
}